import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { InternalLayout } from "@/components/InternalLayout";
import { StatusPill } from "@/components/StatusPill";
import { PHASES, PHASE_LABELS, fmtDate } from "@/lib/constants";
import { ArrowUpRight, AlertTriangle, ClipboardCheck, Hammer } from "lucide-react";

export default function Dashboard() {
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/dashboard").then((r) => setData(r.data)).catch(() => {});
  }, []);

  return (
    <InternalLayout>
      <div className="mb-8" data-testid="dashboard-page">
        <div className="font-mono text-[11px] uppercase tracking-[0.2em] text-muted-foreground mb-2">Lagezentrum</div>
        <h1 className="font-display text-3xl md:text-4xl text-foreground">Dashboard</h1>
        <p className="text-sm text-muted-foreground mt-2 max-w-xl">
          Status aller Projekte, offene Findings und Freigaben. Klicke auf eine Phase, um zu filtern.
        </p>
      </div>

      {data && (
        <>
          {/* KPIs */}
          <div className="akte-grid mb-8">
            <div className="akte-cell col-span-12 md:col-span-3">
              <div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">Projekte aktiv</div>
              <div className="font-display text-4xl mt-2 tabular text-foreground" data-testid="kpi-projects">{data.totals.projects}</div>
            </div>
            <div className="akte-cell col-span-12 md:col-span-3">
              <div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">Offene Findings</div>
              <div className={`font-display text-4xl mt-2 tabular ${data.totals.open_findings > 0 ? "text-destructive" : "text-foreground"}`} data-testid="kpi-findings">
                {data.totals.open_findings}
              </div>
            </div>
            <div className="akte-cell col-span-12 md:col-span-3">
              <div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">Offene Freigaben</div>
              <div className="font-display text-4xl mt-2 tabular text-foreground" data-testid="kpi-approvals">{data.totals.open_approvals}</div>
            </div>
            <div className="akte-cell col-span-12 md:col-span-3">
              <div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">Builds (24h)</div>
              <div className="font-display text-4xl mt-2 tabular text-foreground" data-testid="kpi-builds">{data.recent_builds.length}</div>
            </div>
          </div>

          {/* Phase distribution */}
          <div className="bg-card border border-border mb-8">
            <div className="border-b border-border px-5 py-3">
              <div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">Verteilung nach Phase</div>
            </div>
            <div className="grid grid-cols-7 gap-px bg-border">
              {PHASES.map((p) => (
                <div key={p} className="bg-card px-4 py-5">
                  <div className="text-[11px] font-mono uppercase text-muted-foreground">{PHASE_LABELS[p]}</div>
                  <div className="font-display text-2xl mt-1 tabular text-foreground" data-testid={`phase-count-${p}`}>{data.by_phase[p] || 0}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Projects table */}
          <div className="bg-card border border-border mb-8" data-testid="projects-table">
            <div className="border-b border-border px-5 py-3 flex items-center justify-between">
              <div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">Projekte</div>
              <Link to="/projects" className="text-xs text-primary hover:underline inline-flex items-center gap-1">
                Alle anzeigen <ArrowUpRight className="w-3 h-3" />
              </Link>
            </div>
            <table className="w-full text-sm">
              <thead className="border-b border-border bg-secondary">
                <tr className="text-left">
                  <th className="px-5 py-2 font-mono text-[10px] uppercase tracking-widest text-muted-foreground">Projekt</th>
                  <th className="px-5 py-2 font-mono text-[10px] uppercase tracking-widest text-muted-foreground">Kunde</th>
                  <th className="px-5 py-2 font-mono text-[10px] uppercase tracking-widest text-muted-foreground">Phase</th>
                  <th className="px-5 py-2 font-mono text-[10px] uppercase tracking-widest text-muted-foreground">Aktualisiert</th>
                </tr>
              </thead>
              <tbody>
                {data.projects.length === 0 && (
                  <tr><td colSpan={4} className="px-5 py-8 text-center text-muted-foreground text-sm">Keine Projekte. Lege das erste Projekt an.</td></tr>
                )}
                {data.projects.map((p) => (
                  <tr key={p.project_id} className="border-b border-border hover:bg-secondary">
                    <td className="px-5 py-3">
                      <Link to={`/projects/${p.project_id}`} className="font-medium text-foreground hover:text-primary">{p.name}</Link>
                      <div className="font-mono text-[10px] text-muted-foreground mt-0.5">{p.project_id}</div>
                    </td>
                    <td className="px-5 py-3 text-foreground">{p.customer_org}</td>
                    <td className="px-5 py-3"><StatusPill kind="phase" value={p.current_phase} /></td>
                    <td className="px-5 py-3 font-mono text-xs text-muted-foreground">{fmtDate(p.updated_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Recent activity */}
          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-card border border-border">
              <div className="border-b border-border px-5 py-3 flex items-center gap-2">
                <Hammer className="w-3.5 h-3.5 text-muted-foreground" />
                <div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">Letzte Builds</div>
              </div>
              <div className="divide-y divide-border">
                {data.recent_builds.length === 0 && <div className="px-5 py-6 text-sm text-muted-foreground">Noch keine Builds.</div>}
                {data.recent_builds.map((b) => (
                  <div key={b.run_id} className="px-5 py-3 flex items-center justify-between">
                    <div>
                      <div className="text-sm font-medium text-foreground">{b.name}</div>
                      <div className="font-mono text-[10px] text-muted-foreground">{b.run_id} · {fmtDate(b.started_at)}</div>
                    </div>
                    <StatusPill kind="status" value={b.status} sharp />
                  </div>
                ))}
              </div>
            </div>
            <div className="bg-card border border-border">
              <div className="border-b border-border px-5 py-3 flex items-center gap-2">
                <ClipboardCheck className="w-3.5 h-3.5 text-muted-foreground" />
                <div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">Letzte Audits</div>
              </div>
              <div className="divide-y divide-border">
                {data.recent_audits.length === 0 && <div className="px-5 py-6 text-sm text-muted-foreground">Noch keine Audits.</div>}
                {data.recent_audits.map((a) => (
                  <div key={a.audit_id} className="px-5 py-3 flex items-center justify-between">
                    <div>
                      <div className="text-sm font-medium text-foreground">{a.type === "acceptance" ? "Acceptance Audit" : "Health Check"}</div>
                      <div className="font-mono text-[10px] text-muted-foreground">{a.audit_id} · {fmtDate(a.created_at)}</div>
                    </div>
                    <StatusPill kind="status" value={a.status} sharp />
                  </div>
                ))}
              </div>
            </div>
          </div>
        </>
      )}
    </InternalLayout>
  );
}
